import { type ReactNode, useRef } from 'react';

import { DraftManagerProvider } from '@erp-core/drafts/DraftManager';
import { FocusManagerProvider } from '@erp-core/focus/FocusManager';
import { KeyboardEngineProvider } from '@erp-core/keyboard/KeyboardEngine';
import { ModalManagerProvider } from '@erp-core/modals/ModalManager';
import { ShortcutManagerProvider } from '@erp-core/shortcuts/ShortcutManager';

import { env } from '@config/env';

import { ERPCoreContext } from './ERPCoreContext';

// ─── Provider ─────────────────────────────────────────────────────────────────

interface ERPCoreProviderProps {
  children: ReactNode;
}

/**
 * Root provider for the ERP Core layer.
 *
 * Mount ONCE near the app root — inside QueryProvider, outside the router,
 * so all ERP Core managers can access both data and routing context.
 *
 * Current managers wired (outermost → innermost):
 *   ✓ KeyboardEngine    — central event pipeline, pause/resume, normalization
 *   ✓ ShortcutManager   — keyboard shortcut registry + dispatch
 *   ✓ FocusManager      — focus scope tracking + restoration
 *   ✓ ModalManager      — dialog stack, Esc key, close guards, z-index
 *   ✓ DraftManager      — form autosave, draft restore, expiry pruning
 *
 * Note: FormNavigationManager is per-form (not global) — use FormNavigationProvider.
 *
 * Future additions:
 *   - ERPTableEngine    — table keyboard navigation
 */
export function ERPCoreProvider({ children }: ERPCoreProviderProps) {
  const initialized = useRef(true);

  return (
    <ERPCoreContext.Provider value={{ initialized: initialized.current }}>
      {/*
        KeyboardEngineProvider MUST be the outermost provider so its capture-phase
        keydown listener registers FIRST. This ensures:
          1. pause() calls stopImmediatePropagation — blocks ALL subsequent managers.
          2. High-priority engine handlers fire before ShortcutManager sees the event.
        debug mode is enabled in development for live keyboard event logging.
      */}
      <KeyboardEngineProvider capture debug={env.IS_DEV}>
        {/*
          ShortcutManagerProvider attaches its capture-phase listener second.
          Its registration order gives KeyboardEngine authority over it.
        */}
        <ShortcutManagerProvider capture>
          {/*
          FocusManagerProvider is nested inside ShortcutManagerProvider so that
          focus-related shortcuts (e.g. Tab trap, Esc to close) can use both
          managers in the same component tree.
        */}
          <FocusManagerProvider>
            {/*
            ModalManagerProvider is nested inside FocusManagerProvider so that
            Esc-triggered close coordinates with focus restoration:
            modal closes → onClose() sets isOpen=false → useDialogFocusScope
            deactivates → FocusRegistry restores focus to the trigger element.
          */}
            <ModalManagerProvider>
              {/*
              DraftManagerProvider is the innermost provider — deepest in the
              tree. It auto-detects localStorage availability and falls back to
              memory storage. All form autosave flows use this provider.
            */}
              <DraftManagerProvider>{children}</DraftManagerProvider>
            </ModalManagerProvider>
          </FocusManagerProvider>
        </ShortcutManagerProvider>
      </KeyboardEngineProvider>
    </ERPCoreContext.Provider>
  );
}
